/**
 * CompatResult — standalone compatibility result page at /compat/:id.
 * This is what the bot's "Open in Mini App" deep link resolves to. Readable by
 * both parties (inviter + recipient); the result is anonymized for gifts.
 */
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../components/chrome/MeSubpageShell';
import { CompatResultView } from '../components/flows/Compat/CompatResultView';
import { useCompatResult } from '../hooks/useCompatResult';

export function CompatResult(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslation('common');
  const { data, isLoading, isError, refetch } = useCompatResult(id);

  const refresh = (
    <button type="button" onClick={() => refetch()} style={{ marginTop: 12 }} data-testid="compat-result-refresh">
      {t('actions.retry')}
    </button>
  );

  return (
    <MeSubpageShell title={t('results.compatTitle')} data-testid="compat-result-page">
      {isLoading && <p>{t('states.loading')}</p>}
      {isError && (
        <div role="alert">
          <p>{t('errors.load')}</p>
          {refresh}
        </div>
      )}
      {!isLoading && !isError && data?.status === 'pending' && (
        <div>
          <p>{t('results.stillPreparing')}</p>
          {refresh}
        </div>
      )}
      {!isLoading && !isError && data?.status === 'failed' && (
        <p role="alert">{t('results.couldNotGenerate')}</p>
      )}
      {!isLoading && !isError && data?.status === 'completed' && data.check && (
        <CompatResultView output={data.check} />
      )}
    </MeSubpageShell>
  );
}
